"use client";

import {forwardRef, useCallback, MouseEvent, ReactNode, HTMLAttributes, ButtonHTMLAttributes, AnchorHTMLAttributes, ElementType, ComponentPropsWithoutRef} from 'react';
import {useAnalyticsSafe, AnalyticsEventName} from '../../context/AnalyticsContext';

export interface AnalyticsEventProps {
    analyticsEvent?: AnalyticsEventName;
    analyticsProperties?: Record<string, unknown>;
}

type AsType = 'button' | 'div' | 'a';

type BaseProps<T extends AsType> = AnalyticsEventProps & {
    as?: T;
    onClick?: (event: MouseEvent<HTMLElement>) => void;
    children?: ReactNode;
};

type ButtonElementProps = BaseProps<'button'> & Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'onClick'>;
type DivElementProps = BaseProps<'div'> & Omit<HTMLAttributes<HTMLDivElement>, 'onClick'>;
type AnchorElementProps = BaseProps<'a'> & Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'onClick'> & { href?: string };

export type AnalyticsEventComponentProps = ButtonElementProps | DivElementProps | AnchorElementProps;

const AnalyticsEvent = forwardRef<HTMLElement, AnalyticsEventComponentProps>(
    function AnalyticsEvent(props, ref) {
        const {
            as = 'button',
            analyticsEvent,
            analyticsProperties,
            onClick,
            children,
            ...rest
        } = props;

        const {track} = useAnalyticsSafe();

        const handleClick = useCallback(
            (event: MouseEvent<HTMLElement>) => {
                if (analyticsEvent) {
                    const enrichedProperties = {...analyticsProperties};

                    // Auto-enrich with href for anchor elements
                    if (as === 'a' && 'href' in rest && rest.href) {
                        enrichedProperties.link_href = rest.href;
                    }

                    track(analyticsEvent, enrichedProperties);
                }

                onClick?.(event);
            },
            [analyticsEvent, analyticsProperties, onClick, track, as, rest]
        );

        const Tag = as as ElementType;

        // Add role and tabIndex for div elements to maintain accessibility
        if (as === 'div') {
            return (
                <Tag
                    ref={ref}
                    onClick={handleClick}
                    role="button"
                    tabIndex={(rest as HTMLAttributes<HTMLDivElement>).tabIndex ?? 0}
                    {...rest}
                >
                    {children}
                </Tag>
            );
        }

        return (
            <Tag ref={ref} onClick={handleClick} {...rest}>
                {children}
            </Tag>
        );
    }
);

export default AnalyticsEvent;
